import { useEffect, useState, Suspense, lazy } from 'react';
import { Link } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const NetworkBackground = lazy(() => import('../../components/NetworkBackground'));

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface Project {
  id: string;
  title: string;
  client_name: string;
  category: string;
  description: string;
  technologies: string[];
  image_url: string;
  project_date: string;
  is_featured: boolean;
  is_published?: boolean;
}

interface Notification {
  id: string;
  title: string;
  content: string;
  category: string;
  published_date: string;
}

export default function HomePage() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('すべて');
  const [isScrolled, setIsScrolled] = useState(false);
  const [displayCount, setDisplayCount] = useState(6);

  useEffect(() => {
    fetchProjects();
    fetchNotifications();
  }, []);

  const fetchProjects = async () => {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('is_published', true)
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error fetching projects:', error);
    } else {
      setProjects(data || []);
    }
  };

  const fetchNotifications = async () => {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .order('published_date', { ascending: false })
      .limit(3);

    if (error) {
      console.error('Error fetching notifications:', error);
    } else {
      setNotifications(data || []);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const categories = ['すべて', ...Array.from(new Set(projects.map(p => p.category)))];
  
  const filteredProjects = selectedCategory === 'すべて' 
    ? projects 
    : projects.filter(p => p.category === selectedCategory);

  const displayedProjects = filteredProjects.slice(0, displayCount);
  const hasMore = filteredProjects.length > displayCount;

  const loadMore = () => {
    setDisplayCount(prev => prev + 6);
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case '重要':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'サービス':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case '採用':
        return 'bg-green-50 text-green-700 border-green-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header isTransparent={true} />

      {/* Hero Section */}
      <section id="home" className="relative h-screen flex items-center justify-center">
        <div className="absolute inset-0">
          <img src="https://public.readdy.ai/ai/img_res/da1a81a98cea296694bbfbeb8aceba91.jpg" alt="Array Venture" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        <div className="relative z-10 text-center px-4 w-full max-w-5xl mx-auto">
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
            アプリケーションからインフラまで<br />ビジネスを支えるITソリューション
          </h2>
          <p className="text-lg md:text-xl text-white/90 mb-10 max-w-3xl mx-auto">
            私たちは、最先端の技術と柔軟な対応力で、世の中の課題を解決します
          </p>
        </div>
      </section>

      {/* Notifications Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-3xl font-bold text-gray-900">お知らせ</h3>
          </div>

          {notifications.length > 0 ? (
            <div className="space-y-4">
              {notifications.map((notification) => (
                <Link
                  key={notification.id}
                  to={`/notification/${notification.id}`}
                  className="block bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow duration-300 cursor-pointer"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 text-gray-400 pt-1">
                      <i className="ri-notification-line text-xl"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-sm text-gray-500">
                          {new Date(notification.published_date).toLocaleDateString('ja-JP', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit'
                          })}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getCategoryColor(notification.category)}`}>
                          {notification.category}
                        </span>
                      </div>
                      <h4 className="text-lg font-semibold text-gray-900">
                        {notification.title}
                      </h4>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <i className="ri-notification-off-line text-4xl text-gray-300 mb-3"></i>
              <p className="text-gray-500">現在、お知らせはありません</p>
            </div>
          )}
        </div>
      </section>

      {/* Mission Section with Network Background */}
      <section className="relative py-20 bg-gradient-to-b from-white via-sky-50/30 to-white overflow-hidden">
        <Suspense fallback={null}>
          <NetworkBackground />
        </Suspense>
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 mb-4 tracking-wide">
              MISSION
            </h3>
            <p className="text-2xl md:text-3xl font-bold text-teal-600 mb-12">
              未来を共創する
            </p>
          </div>
          <div className="space-y-12 md:space-y-16">
            <div className="text-center">
              <h4 className="text-2xl md:text-3xl font-bold text-teal-600 mb-4 md:mb-6">
                技術でつなぐ未来
              </h4>
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto">
                私たちは技術を通じて、人と社会をより良い方向へつなぐ架け橋となることを目指しています。
              </p>
            </div>
            <div className="text-center">
              <h4 className="text-2xl md:text-3xl font-bold text-teal-600 mb-4 md:mb-6">
                Issueを解決する
              </h4>
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto">
                課題や目標を深く理解し、最適なソリューションを提供することで価値を創造します。
              </p>
            </div>
            <div className="text-center">
              <h4 className="text-2xl md:text-3xl font-bold text-teal-600 mb-4 md:mb-6">
                継続的な成長
              </h4>
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto">
                技術の進化を取り入れ続け、革新を起こす企業であり続けます。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">事業内容</h3>
          </div>

          <div className="space-y-8">
            {/* System Development Support */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="h-64 md:h-auto">
                  <img src="https://static.readdy.ai/image/1b7ec789b943e12f393f86a47bf59f58/129090f521d94abbc456e2168cc1f31a.png" alt="Infrastructure" className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <h4 className="text-2xl font-bold text-gray-900 mb-4">システム開発支援</h4>
                  <p className="text-gray-600 mb-6">
                    アプリケーションからインフラ基盤まで、お客様の要件に最適なシステム開発を支援します。
                  </p>
                  <div className="flex flex-wrap gap-2 mb-6">
                    <span className="px-4 py-2 bg-teal-50 text-teal-700 rounded-lg text-sm font-medium">Webアプリ/ スマホアプリ</span>
                    <span className="px-4 py-2 bg-teal-50 text-teal-700 rounded-lg text-sm font-medium">クラウドインフラ基盤</span>
                    <span className="px-4 py-2 bg-teal-50 text-teal-700 rounded-lg text-sm font-medium">基幹システム</span>
                    <span className="px-4 py-2 bg-teal-50 text-teal-700 rounded-lg text-sm font-medium">データ基盤</span>
                  </div>
                  <button 
                    onClick={() => scrollToSection('projects')} 
                    className="bg-teal-500 text-white px-6 py-3 rounded-lg text-sm font-semibold hover:bg-teal-600 transition-all transform hover:scale-105 shadow-md w-fit cursor-pointer"
                  >
                    詳細はこちら
                  </button>
                </div>
              </div>
            </div>

            {/* CancelBill */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="order-2 md:order-1 p-8 md:p-12 flex flex-col justify-center">
                  <h4 className="text-2xl font-bold text-gray-900 mb-4">キャンセルビル</h4>
                  <p className="text-gray-600 mb-6">
                    無断・直前キャンセル料回収サービス
                  </p>
                  <Link 
                    to="https://info.cancel-bill.com/"
                    className="bg-teal-500 text-white px-6 py-3 rounded-lg text-sm font-semibold hover:bg-teal-600 transition-all transform hover:scale-105 shadow-md w-fit cursor-pointer"
                  >
                    詳細はこちら
                  </Link>
                </div>
                <div className="order-1 md:order-2 h-64 md:h-auto">
                  <img src="https://static.readdy.ai/image/1b7ec789b943e12f393f86a47bf59f58/7ac47f46c009a1638022b02b2b19eed8.png" alt="CancelBill" className="w-full h-full object-cover object-top" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">実績・事例</h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              これまでに手がけた多様なプロジェクトをご紹介します。お客様のビジネス課題を解決し、成果を上げてきた実績です。
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => {
                  setSelectedCategory(category);
                  setDisplayCount(6);
                }}
                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  selectedCategory === category
                    ? 'bg-teal-500 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Projects Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {displayedProjects.map((project) => (
              <Link
                key={project.id}
                to={`/project/${project.id}`}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 cursor-pointer"
              >
                <div className="relative h-56 w-full overflow-hidden">
                  <img
                    src={project.image_url}
                    alt={project.title}
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300"
                  />
                  {project.is_featured && (
                    <div className="absolute top-4 right-4 bg-teal-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      注目
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-3 py-1 bg-teal-50 text-teal-700 rounded-lg text-xs font-medium">
                      {project.category}
                    </span>
                    <span className="text-xs text-gray-500">
                      {new Date(project.project_date).toLocaleDateString('ja-JP', {
                        year: 'numeric',
                        month: 'long'
                      })}
                    </span>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2">
                    {project.title}
                  </h4>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                    {project.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.technologies.slice(0, 3).map((tech, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs"
                      >
                        {tech}
                      </span>
                    ))}
                    {project.technologies.length > 3 && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                        +{project.technologies.length - 3}
                      </span>
                    )}
                  </div>
                  <div className="pt-4 border-t border-gray-100">
                    <p className="text-sm text-gray-500">
                      <strong className="text-gray-700">クライアント:</strong> {project.client_name}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {filteredProjects.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">該当するプロジェクトがありません</p>
            </div>
          )}

          {hasMore && (
            <div className="mt-12 text-center">
              <button
                onClick={loadMore}
                className="bg-gray-100 text-gray-700 px-10 py-4 rounded-lg text-base font-semibold hover:bg-gray-200 transition-all transform hover:scale-105 shadow-md whitespace-nowrap cursor-pointer"
              >
                さらに読み込む
              </button>
            </div>
          )}
        </div>
      </section>

      <Footer />

      {/* Scroll to Top Button */}
      <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className={`fixed bottom-8 right-8 w-12 h-12 bg-teal-500 text-white rounded-full shadow-lg hover:bg-teal-600 transition-all flex items-center justify-center cursor-pointer ${isScrolled ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} aria-label="ページトップへ">
        <i className="ri-arrow-up-line text-xl"></i>
      </button>
    </div>
  );
}
